// The fine rotation control: a quarter-turn button that reveals a vertical
// slider, a counter-rotate button and a numeric entry on hover. Shared by the
// single-slide header (which turns the slide) and the compare overlay (which
// turns whichever pane is selected, to bring two slides into register).

import { useEffect, useRef, useState } from 'react';
import { RotateCcw, RotateCw } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

const SLIDER_H = 120;

/**
 * Vertical drag that survives leaving the track. The slider lives in a panel that
 * only exists while hovered, so sliding sideways used to hide it mid-drag — and a
 * hidden element measures 0x0, which broke the position maths and stranded the
 * handle. Measure the track ONCE on press and follow clientY from window events
 * until release, so only vertical movement matters and horizontal drift is
 * ignored. `onDragChange` lets the panel stay open for the duration.
 */
export function useTrackDrag(
  trackRef: React.RefObject<HTMLDivElement>,
  applyFraction: (f: number) => void,
  onDragChange?: (dragging: boolean) => void,
  /** Stop at the ends of the track. True for a bounded quantity like opacity;
   *  false for rotation, which has no ends — dragging past the top just keeps
   *  turning, and the angle wraps back into the track on its own. */
  clamp = true,
  /** Which way the track runs. The other axis is ignored for the whole gesture,
   *  so drifting sideways never interrupts it. */
  axis: 'x' | 'y' = 'y',
) {
  return (e: React.PointerEvent) => {
    e.preventDefault();
    const track = trackRef.current;
    const box = track?.getBoundingClientRect();
    const span = axis === 'y' ? box?.height : box?.width;
    if (!track || !box || !span) return;

    // Capture the pointer so the gesture belongs to this element until release,
    // wherever it wanders. Without it a real mouse can hand the drag to
    // something else on the way past — a native image/text drag starting fires
    // pointercancel, and the slider would simply stop following.
    try {
      track.setPointerCapture(e.pointerId);
    } catch {
      // capture unsupported / pointer already gone; window listeners still cover it
    }

    const at = (client: number) => {
      const f = (client - (axis === 'y' ? box.top : box.left)) / span;
      applyFraction(clamp ? Math.max(0, Math.min(1, f)) : f);
    };
    const coord = (ev: { clientX: number; clientY: number }) =>
      axis === 'y' ? ev.clientY : ev.clientX;
    onDragChange?.(true);
    at(coord(e));
    const move = (ev: PointerEvent) => at(coord(ev));
    const end = () => {
      onDragChange?.(false);
      try {
        track.releasePointerCapture(e.pointerId);
      } catch {
        // already released
      }
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
  };
}

export function RotateControl({ deg, onRotate }: { deg: number; onRotate: (deg: number) => void }) {
  // Guarded: a caller reading a pane that has not reported a rotation yet hands
  // us undefined, and NaN here propagates all the way to view.setRotation(NaN),
  // which leaves the map with no drawable tile coordinates at all.
  const norm = Number.isFinite(deg) ? ((Math.round(deg) % 360) + 360) % 360 : 0;
  // Hover alone would close the panel the moment the pointer slides off it.
  const [dragging, setDragging] = useState(false);
  // The two buttons jump to the next quarter turn in their direction (not a fixed
  // ±90 add): CCW → previous multiple of 90, CW → next multiple of 90.
  const stepCcw = () => onRotate((Math.ceil(norm / 90) - 1) * 90);
  const stepCw = () => onRotate((Math.floor(norm / 90) + 1) * 90);
  return (
    <div className="group relative">
      {/* Top button (the existing toolbar button): rotate CCW to the next 90°.
          Hovering also opens the fine-control panel below. */}
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={stepCcw}
            className={cn(
              'inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors',
              'text-muted-foreground hover:bg-accent hover:text-foreground',
              'group-hover:rounded-b-none group-hover:border group-hover:border-b-0 group-hover:bg-popover group-hover:text-foreground group-hover:shadow-md',
            )}
          >
            <RotateCcw className="h-4 w-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top">Rotate left ([)</TooltipContent>
      </Tooltip>
      {/* Attached panel — same width as the button, flush under it (seamless, like
          the shapes dropdown): fine slider, the rotate-right button, and a numeric
          entry. */}
      <div
        className={cn(
          'absolute left-0 top-full z-30 w-8 flex-col items-center overflow-hidden rounded-b-md border border-t-0 bg-popover shadow-md',
          dragging ? 'flex' : 'hidden group-hover:flex',
        )}
      >
        <RotationSlider deg={norm} onChange={onRotate} onDragChange={setDragging} />
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={stepCw}
              className="flex h-8 w-full items-center justify-center text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              <RotateCw className="h-4 w-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="left">Rotate right (])</TooltipContent>
        </Tooltip>
        <RotationInput deg={norm} onChange={onRotate} />
      </div>
    </div>
  );
}

// Vertical slider. The middle is 0°; the handle travels up to 180° CCW and down
// to 180° CW. The stored angle is [0, 360), so map it to a signed [-180, 180]
// offset from center (angles > 180 read as negative / CCW).
function RotationSlider({
  deg,
  onChange,
  onDragChange,
}: {
  deg: number;
  onChange: (deg: number) => void;
  onDragChange?: (dragging: boolean) => void;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const signed = deg > 180 ? deg - 360 : deg; // [-180, 180], up = negative (CCW)
  const y = (signed / 360 + 0.5) * SLIDER_H; // middle when signed === 0

  const onPointerDown = useTrackDrag(
    trackRef,
    // centre = 0°, down = CW. `f` is unbounded, so dragging past either end
    // carries on turning — 500° simply wraps to 140° and the handle reappears at
    // the matching place on the track.
    (f) => onChange((((Math.round((f - 0.5) * 360)) % 360) + 360) % 360),
    onDragChange,
    false,
  );

  return (
    <div
      ref={trackRef}
      onPointerDown={onPointerDown}
      className="relative my-1 w-4 cursor-pointer select-none touch-none"
      draggable={false}
      onDragStart={(ev) => ev.preventDefault()}
      style={{ height: SLIDER_H }}
    >
      {/* Track line */}
      <div className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-border" />
      {/* Center tick (0°) */}
      <div className="absolute left-1/2 top-1/2 h-px w-2 -translate-x-1/2 -translate-y-1/2 bg-border" />
      {/* Handle */}
      <div
        className="absolute left-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border border-primary-foreground bg-primary shadow"
        style={{ top: y }}
      />
    </div>
  );
}

// Numeric entry — type an angle and press Enter (or blur). Wraps into [0, 360).
function RotationInput({ deg, onChange }: { deg: number; onChange: (deg: number) => void }) {
  const [text, setText] = useState(String(deg));
  const focused = useRef(false);
  // Follow the live rotation unless the user is mid-edit.
  useEffect(() => {
    if (!focused.current) setText(String(deg));
  }, [deg]);

  const apply = () => {
    const n = Number(text.trim());
    if (Number.isFinite(n)) onChange(((Math.round(n) % 360) + 360) % 360);
    else setText(String(deg));
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <input
          value={text}
          inputMode="numeric"
          onChange={(e) => setText(e.target.value)}
          onFocus={(e) => {
            focused.current = true;
            e.target.select();
          }}
          onBlur={() => {
            focused.current = false;
            apply();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              apply();
              (e.target as HTMLInputElement).blur();
            }
          }}
          className="h-6 w-full border-t border-border bg-background text-center text-xs tabular-nums text-foreground outline-none focus:bg-accent"
        />
      </TooltipTrigger>
      <TooltipContent side="left">Enter degrees</TooltipContent>
    </Tooltip>
  );
}


/** A horizontal slider in the same visual language as RotationSlider, for any
 *  0..1 quantity. Used for the overlay's A↔B mix, where left and right line up
 *  with the A and B buttons it sits between. */
export function HorizontalSlider({
  value,
  onChange,
  label,
  detail,
  width = 72,
  stops = 0,
  onDragChange,
}: {
  value: number;
  onChange: (v: number) => void;
  label: string;
  /** Second tooltip line — the live value, typically. */
  detail?: string;
  width?: number;
  /** Draw this many evenly spaced ticks, ends included. Used by the compare mix
   *  to mark where each slide sits along the fade. 0 draws a centre tick. */
  stops?: number;
  /** A drag began or ended: a hover panel holding the slider stays open meanwhile. */
  onDragChange?: (dragging: boolean) => void;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const onPointerDown = useTrackDrag(trackRef, onChange, onDragChange, true, 'x');
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          ref={trackRef}
          onPointerDown={onPointerDown}
          aria-label={label}
          className="relative mx-2 h-8 cursor-pointer select-none touch-none"
          draggable={false}
          onDragStart={(ev) => ev.preventDefault()}
          style={{ width }}
        >
          <div className="absolute left-0 top-1/2 h-px w-full -translate-y-1/2 bg-border" />
          {(stops > 1 ? Array.from({ length: stops }, (_, i) => i / (stops - 1)) : [0.5]).map((f) => (
            <div
              key={f}
              className="absolute top-1/2 h-2 w-px -translate-x-1/2 -translate-y-1/2 bg-border"
              style={{ left: `${f * 100}%` }}
            />
          ))}
          <div
            className="absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border border-primary-foreground bg-primary shadow"
            style={{ left: value * width }}
          />
        </div>
      </TooltipTrigger>
      <TooltipContent side="top">
        {label}
        {detail && <div className="text-[10px] text-muted-foreground">{detail}</div>}
      </TooltipContent>
    </Tooltip>
  );
}
